import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function AboutPage() {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/a-propos`,
      "url": `${import.meta.env.VITE_SITE_URL}/a-propos`,
      "name": "À propos de JLD Concept SRL - Partenaire Télécom & Énergie Belgique",
      "description": "Découvrez JLD Concept SRL, votre partenaire de confiance pour les solutions télécom Orange Belgique et énergie verte Eneco. Plus de 2000 consultants actifs en Belgique.",
      "inLanguage": "fr-BE",
      "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL}/#website` },
      "about": {
        "@type": "Organization",
        "name": "JLD Concept SRL",
        "description": "Partenaire télécom et énergie en Belgique, filiale Smart Services Network du groupe Orange.",
        "foundingDate": "2014",
        "numberOfEmployees": { "@type": "QuantitativeValue", "value": 2000 }
      }
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-about';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-about')?.remove(); };
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />

      <div className="pt-24">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-orange-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-block text-sm font-semibold text-orange-600 bg-orange-100 px-4 py-2 rounded-full mb-4">
                À PROPOS DE NOUS
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                JLD Concept SRL
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Votre partenaire de confiance pour les solutions télécom et énergie en Belgique
              </p>
            </div>
          </div>
        </section>

        {/* Advantages for Clients */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Nos avantages pour vous en tant que client
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-xl mb-6">
                  <i className="ri-shield-check-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">🔸 Garantie</h3>
                <p className="text-gray-600 leading-relaxed">
                  Chez SMART, nous vous offrons des services d'excellente qualité. Pour garantir 
                  cette qualité, nous travaillons avec des partenaires fiables tels que Orange et Eneco.
                </p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-xl mb-6">
                  <i className="ri-user-smile-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">🔸 Facilité</h3>
                <p className="text-gray-600 leading-relaxed">
                  Chez SMART, vous avez toujours une personne de contact privilégiée. Votre consultant 
                  vous aide à réaliser des économies et reste également à votre disposition si vous 
                  rencontrez un problème inattendu. Simple et efficace!
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-xl mb-6">
                  <i className="ri-price-tag-3-line text-3xl text-blue-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">🔸 Bon marché</h3>
                <p className="text-gray-600 leading-relaxed">
                  Chez SMART, pas de campagnes publicitaires coûteuses ni de bureaux et points de vente 
                  luxueux dont l'entretien coûte une fortune. SMART mise sur le bouche-à-oreille de ses 
                  clients satisfaits.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Advantages for Consultants */}
        <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-12">
              <div className="inline-block text-sm font-semibold bg-orange-500/20 text-orange-300 px-4 py-2 rounded-full mb-4">
                POUR LES CONSULTANTS
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 max-w-3xl">
                Nos avantages pour vous en tant que consultant
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl leading-relaxed">
                Rejoignez une communauté de plus de 2000 consultants qui ont déjà fait le choix 
                d'un second job avec Smart Services Network.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/15 transition-all">
                <div className="w-16 h-16 flex items-center justify-center bg-orange-500/20 rounded-xl mb-6">
                  <i className="ri-time-line text-3xl text-orange-400"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">🔸 Liberté</h3>
                <p className="text-gray-300 leading-relaxed">
                  Chez SMART, vous avez la liberté de déterminer vous-même votre agenda. Vous disposez 
                  ainsi de plus de temps pour les choses qui sont importantes pour vous.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/15 transition-all">
                <div className="w-16 h-16 flex items-center justify-center bg-green-500/20 rounded-xl mb-6">
                  <i className="ri-emotion-happy-line text-3xl text-green-400"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">🔸 Fun</h3>
                <p className="text-gray-300 leading-relaxed">
                  Chez SMART, vous pouvez travailler avec votre famille et vos amis. Une carrière chez 
                  SMART, c'est combiner l'utile à l'agréable : le travail devient un plaisir.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/15 transition-all">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-500/20 rounded-xl mb-6">
                  <i className="ri-hand-heart-line text-3xl text-blue-400"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">🔸 Accompagnement</h3>
                <p className="text-gray-300 leading-relaxed">
                  Chez SMART, un nouveau consultant bénéficie d'un accompagnement exclusif. Vous avez 
                  toujours accès aux dernières informations et nous veillons également à ce que vous 
                  bénéficiiez d'une formation complète et de qualité.
                </p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-8">
              <div
                className="w-full md:w-1/3 h-64 bg-cover bg-center rounded-2xl"
                style={{
                  backgroundImage: 'url(https://public.readdy.ai/ai/img_res/8af1c96e73b7bd501f3f8025d2955ce5.jpg)',
                }}
              ></div>

              <div className="flex-1">
                <div className="text-6xl font-bold mb-4">2000+</div>
                <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                  Consultants ont déjà choisi de développer un revenu complémentaire avec Smart Services Network. 
                  Rejoignez cette communauté dynamique et construisez votre avenir à votre rythme.
                </p>
                <a
                  href="/devenir-consultant"
                  className="inline-block px-8 py-4 bg-white text-gray-900 font-semibold rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap"
                >
                  Devenir Consultant
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-gradient-to-br from-orange-50 to-green-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Notre mission
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  JLD Concept SRL travaille en collaboration avec Smart Services Network en Belgique, 
                  acteur majeur dans le domaine du télécom avec Orange Belgique et de l'énergie avec Eneco.
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Notre objectif est simple : vous aider à économiser sur vos services quotidiens tout 
                  en vous offrant la possibilité de générer des revenus supplémentaires en devenant consultant.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm">
                    <i className="ri-check-line text-green-600"></i>
                    <span className="text-sm font-medium text-gray-700">Qualité garantie</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm">
                    <i className="ri-check-line text-green-600"></i>
                    <span className="text-sm font-medium text-gray-700">Partenaires fiables</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm">
                    <i className="ri-check-line text-green-600"></i>
                    <span className="text-sm font-medium text-gray-700">Accompagnement personnalisé</span>
                  </div>
                </div>
              </div>

              <div
                className="w-full h-96 bg-cover bg-center rounded-2xl shadow-xl"
                style={{
                  backgroundImage: 'url(https://readdy.ai/api/search-image?query=Modern%20Belgian%20business%20office%20interior%20with%20professional%20team%20collaboration%2C%20bright%20contemporary%20workspace%20with%20natural%20light%2C%20diverse%20colleagues%20working%20together%20in%20open%20plan%20environment&width=800&height=800&seq=mission001&orientation=squarish)',
                }}
              ></div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}